<template>
  <div class="appointments-report-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <!-- Print Button -->
        <div class="print-button-container">
          <button @click="printReport" class="print-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,8H5C3.9,8 3,8.9 3,10V17H7V21H17V17H21V10C21,8.9 20.1,8 19,8M17,19H7V14H17V19M19,12C18.4,12 18,11.6 18,11C18,10.4 18.4,10 19,10C19.6,10 20,10.4 20,11C20,11.6 19.6,12 19,12M17,3H7V7H17V3Z"/>
            </svg>
            Print Report
          </button>
        </div>

        <!-- Page Header -->
        <div class="page-header">
          <h1 class="page-title">Appointments Report</h1>
          <p class="page-description">Comprehensive analytics and insights for your appointment bookings</p>
        </div>

        <!-- Period Filters -->
        <div class="filters-section">
          <div class="period-buttons">
            <button
              v-for="period in periods"
              :key="period.value"
              @click="selectPeriod(period.value)"
              :class="['period-btn', { active: selectedPeriod === period.value }]"
            >
              {{ period.label }}
            </button>
          </div>

          <!-- Custom Date Range -->
          <div v-if="selectedPeriod === 'custom'" class="custom-date-range">
            <div class="date-input-group">
              <label>Start Date</label>
              <input type="date" v-model="customStartDate" class="date-input" />
            </div>
            <div class="date-input-group">
              <label>End Date</label>
              <input type="date" v-model="customEndDate" class="date-input" />
            </div>
            <button @click="loadReport" class="btn-apply">Apply</button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>Loading report...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <p>{{ error }}</p>
          <button @click="loadReport" class="btn-retry">Retry</button>
        </div>

        <!-- Report Content -->
        <div v-else-if="reportData" class="report-content">
          <!-- Statistics Cards -->
          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-icon">📊</div>
              <div class="stat-content">
                <div class="stat-number">{{ reportData.statistics.total_appointments }}</div>
                <div class="stat-label">Total Appointments</div>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-icon">📅</div>
              <div class="stat-content">
                <div class="stat-number">{{ reportData.statistics.booked }}</div>
                <div class="stat-label">Booked</div>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-icon">✅</div>
              <div class="stat-content">
                <div class="stat-number">{{ reportData.statistics.completed }}</div>
                <div class="stat-label">Completed</div>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-icon">❌</div>
              <div class="stat-content">
                <div class="stat-number">{{ reportData.statistics.cancelled }}</div>
                <div class="stat-label">Cancelled</div>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-icon">👥</div>
              <div class="stat-content">
                <div class="stat-number">{{ reportData.statistics.unique_customers }}</div>
                <div class="stat-label">Unique Customers</div>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-icon">📈</div>
              <div class="stat-content">
                <div class="stat-number">{{ reportData.statistics.completion_rate }}%</div>
                <div class="stat-label">Completion Rate</div>
              </div>
            </div>
          </div>

          <!-- Charts Section -->
          <div class="charts-section">
            <!-- Appointments by Day of Week -->
            <div class="chart-card">
              <div class="chart-header">
                <h3 class="chart-title">Appointments by Day of Week</h3>
              </div>
              <div class="chart-content">
                <div class="bar-chart">
                  <div
                    v-for="day in reportData.by_day_of_week"
                    :key="day.day"
                    class="bar-item"
                  >
                    <div class="bar-label">{{ day.day }}</div>
                    <div class="bar-container">
                      <div
                        class="bar-fill"
                        :style="{ width: getBarWidth(day.count, maxDayCount) }"
                      >
                        <span class="bar-value">{{ day.count }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Appointments by Service -->
            <div v-if="reportData.by_service.length > 0" class="chart-card">
              <div class="chart-header">
                <h3 class="chart-title">Appointments by Service</h3>
              </div>
              <div class="chart-content">
                <div class="bar-chart">
                  <div
                    v-for="service in reportData.by_service"
                    :key="service.service_name"
                    class="bar-item"
                  >
                    <div class="bar-label">{{ service.service_name }}</div>
                    <div class="bar-container">
                      <div
                        class="bar-fill service"
                        :style="{ width: getBarWidth(service.count, maxServiceCount) }"
                      >
                        <span class="bar-value">{{ service.count }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Top Customers Table -->
          <div class="table-section">
            <div class="table-card">
              <div class="table-header">
                <h3>Top 10 Customers</h3>
              </div>
              <div class="table-content">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Customer Name</th>
                      <th>Phone Number</th>
                      <th>Total Appointments</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(customer, index) in reportData.top_customers" :key="customer.customer_phone">
                      <td>{{ index + 1 }}</td>
                      <td>{{ customer.customer_name }}</td>
                      <td>{{ formatPhone(customer.customer_phone) }}</td>
                      <td><strong>{{ customer.appointment_count }}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Payment Statistics -->
          <div class="payment-stats">
            <div class="stats-grid">
              <div class="stat-card">
                <div class="stat-icon">✅</div>
                <div class="stat-content">
                  <div class="stat-number">{{ reportData.payment_statistics.verified }}</div>
                  <div class="stat-label">Verified Payments</div>
                </div>
              </div>
              <div class="stat-card">
                <div class="stat-icon">⏳</div>
                <div class="stat-content">
                  <div class="stat-number">{{ reportData.payment_statistics.pending }}</div>
                  <div class="stat-label">Pending Payments</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'

export default {
  name: 'AppointmentsReportView',

  components: {
    Sidebar,
    TopHeader
  },

  data () {
    return {
      loading: false,
      error: null,
      reportData: null,
      selectedPeriod: 'week',
      customStartDate: '',
      customEndDate: '',
      periods: [
        { value: 'week', label: 'Last 7 Days' },
        { value: 'month', label: 'Last 30 Days' },
        { value: 'year', label: 'Last Year' },
        { value: 'custom', label: 'Custom Range' }
      ]
    }
  },

  computed: {
    maxDayCount () {
      if (!this.reportData || !this.reportData.by_day_of_week) return 0
      return Math.max(...this.reportData.by_day_of_week.map(d => d.count))
    },

    maxServiceCount () {
      if (!this.reportData || !this.reportData.by_service) return 0
      return Math.max(...this.reportData.by_service.map(s => parseInt(s.count)))
    }
  },

  mounted () {
    this.loadReport()
  },

  methods: {
    async loadReport () {
      try {
        this.loading = true
        this.error = null
        const token = localStorage.getItem('auth_token')

        let url = `https://api.quali-d.com/appointments/report?period=${this.selectedPeriod}`

        if (this.selectedPeriod === 'custom' && this.customStartDate && this.customEndDate) {
          url += `&start_date=${this.customStartDate}&end_date=${this.customEndDate}`
        }

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        const result = await response.json()

        if (result.success) {
          this.reportData = result
        } else {
          this.error = result.message || 'Failed to load report'
        }
      } catch (error) {
        console.error('Error loading report:', error)
        this.error = 'Error loading report'
      } finally {
        this.loading = false
      }
    },

    selectPeriod (period) {
      this.selectedPeriod = period
      if (period !== 'custom') {
        this.loadReport()
      }
    },

    getBarWidth (value, max) {
      if (!max) return '0%'
      return `${(value / max) * 100}%`
    },

    formatPhone (phone) {
      if (!phone) return '-'
      if (phone.startsWith('20')) {
        return `+${phone}`
      }
      return phone
    },

    printReport () {
      window.print()
    }
  }
}
</script>

<style scoped>
.appointments-report-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.print-button-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 24px;
}

.print-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #10b981;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.print-btn:hover {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.page-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  transition: color 0.3s ease;
}

.page-description {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
  transition: color 0.3s ease;
}

/* Filters */
.filters-section {
  background: var(--bg-primary);
  padding: 24px;
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  margin-bottom: 32px;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.period-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.period-btn {
  padding: 10px 20px;
  border: 2px solid var(--border-primary);
  background: var(--bg-primary);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.period-btn:hover {
  border-color: #3b82f6;
  color: #3b82f6;
}

.period-btn.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

.custom-date-range {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border-primary);
}

.date-input-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.date-input-group label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.date-input {
  padding: 10px 14px;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  width: 200px;
  background: var(--bg-primary);
  color: var(--text-primary);
}

.btn-apply {
  padding: 10px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-apply:hover {
  background: #2563eb;
}

/* Loading/Error States */
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px;
  background: var(--bg-primary);
  border-radius: 12px;
  border: 1px solid var(--border-primary);
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-primary);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.btn-retry {
  margin-top: 16px;
  padding: 10px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

/* Statistics Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: var(--bg-primary);
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.stat-icon {
  font-size: 32px;
  opacity: 0.8;
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
  transition: color 0.3s ease;
}

.stat-label {
  font-size: 14px;
  color: var(--text-secondary);
  margin-top: 4px;
  transition: color 0.3s ease;
}

/* Charts Section */
.charts-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.chart-card {
  background: var(--bg-primary);
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.chart-header {
  padding: 24px 24px 16px;
  border-bottom: 1px solid var(--border-primary);
}

.chart-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  transition: color 0.3s ease;
}

.chart-content {
  padding: 24px;
}

.bar-chart {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bar-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.bar-label {
  width: 100px;
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
  transition: color 0.3s ease;
}

.bar-container {
  flex: 1;
  height: 32px;
  background: var(--bg-secondary);
  border-radius: 6px;
  overflow: hidden;
  position: relative;
}

.bar-fill {
  height: 100%;
  background: #3b82f6;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
  min-width: 40px;
  transition: width 0.3s ease;
}

.bar-fill.service {
  background: #8b5cf6;
}

.bar-value {
  color: white;
  font-size: 12px;
  font-weight: 600;
}

/* Tables */
.table-section {
  margin-bottom: 32px;
}

.table-card {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.table-header {
  padding: 24px 24px 16px;
  border-bottom: 1px solid var(--border-primary);
}

.table-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.table-content {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  text-align: left;
  padding: 16px 24px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-primary);
}

.data-table td {
  padding: 16px 24px;
  border-bottom: 1px solid var(--border-secondary);
  font-size: 14px;
  color: var(--text-secondary);
}

.data-table tbody tr:hover {
  background: var(--bg-tertiary);
}

/* Payment Stats */
.payment-stats {
  background: var(--bg-primary);
  padding: 24px;
  border-radius: 12px;
  border: 1px solid var(--border-primary);
}

/* Print Styles */
@media print {
  .appointments-report-layout {
    background: white !important;
  }

  .print-button-container,
  .filters-section {
    display: none !important;
  }

  .main-content {
    margin-left: 0 !important;
  }

  .page-content {
    padding: 0 !important;
  }

  .stat-card,
  .chart-card,
  .table-card,
  .payment-stats {
    background: white !important;
    box-shadow: none !important;
    border: 1px solid #e5e7eb !important;
    page-break-inside: avoid;
  }

  .stats-grid {
    grid-template-columns: repeat(3, 1fr) !important;
    gap: 10px !important;
  }

  .charts-section {
    grid-template-columns: 1fr !important;
    gap: 20px !important;
  }

  .data-table {
    font-size: 12px !important;
  }

  .data-table th,
  .data-table td {
    padding: 8px !important;
  }

  /* Force black text for printing */
  .page-title,
  .stat-number,
  .chart-title,
  .data-table th,
  .data-table td strong {
    color: #000 !important;
  }

  .page-description,
  .stat-label,
  .bar-label,
  .data-table td:not(strong) {
    color: #666 !important;
  }
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .charts-section {
    grid-template-columns: 1fr;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .stat-card {
    padding: 16px;
    gap: 12px;
  }

  .stat-icon {
    font-size: 24px;
  }

  .stat-number {
    font-size: 20px;
  }

  .table-header {
    padding: 16px;
  }

  .data-table th,
  .data-table td {
    padding: 12px 16px;
  }

  .custom-date-range {
    flex-direction: column;
    align-items: stretch;
  }

  .date-input {
    width: 100%;
  }
}
</style>
